import Tooltip from '@mui/material/Tooltip';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import React, { Fragment, useContext, useEffect, useState } from 'react';
import { capitalizeFirstLetter, isEmpty, showAsPercentile, splitCamelCase, splitCamelCaseWithInitials } from '../../../../utils/utils';
import { CONSTANTS } from '../../../../constants/constants';
import NRLoader from '../../../NRLoader/NRLoader';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import { VulnDrawerTabsContext } from '../NRVulnerabilityDetails';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import NRShowMore from '../../../NRShowMore/NRShowMore';
import NRLink from '../../../NRLink/NRLink';
import NRKeyValueDataDisplay from '../../../NRKeyValueDataDisplay/NRKeyValueDataDisplay';
import { useTheme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
const {
  CVSS_V2,
  CVSS_V3,
  CVSS_V4,
  KEYS,
  TOOLTIP_KEYS,
  TITLE,
  TOOLTIP,
  VECTOR,
  EPSS_SCORE,
  EPSS_PERCENTILE,
  EPSS_TOOLTIP_INFO,
  CWE,
  CWE_TOOLTIP_INFO
} = CONSTANTS.TABLE.VULNERABILITIES_TABLE.VULNERABILITY_DETAIL_DRAWER.SEVERITY_AND_METRICS_SECTION;
const { V3, V2, V4 } = CONSTANTS.TABLE.VULNERABILITIES_TABLE;

let tooltipEntries = [
  'attackVector',
  'attackComplexity',
  'privilegesRequired',
  'userInteraction',
  'scope',
  'accessVector',
  'accessComplexity',
  'authentication',
  'confidentialityImpact',
  'integrityImpact',
  'availabilityImpact',
  'attackRequirements',
  'vulnConfidentialityImpact',
  'vulnIntegrityImpact',
  'vulnAvailabilityImpact',
  'subConfidentialityImpact',
  'subIntegrityImpact',
  'subAvailabilityImpact',
  'exploitMaturity',
  'confidentialityRequirement',
  'integrityRequirement',
  'availabilityRequirement',
  'safety',
  'automatable',
  'recovery',
  'valueDensity',
  'vulnerabilityResponseEffort'
];

let orderedColumns = {
  [V4]: ['baseScore', 'vectorString', 'baseThreatScore', 'exploitMaturity', 'automatable', 'source'],
  [V3]: [
    'baseScore', // + BaseSeverity
    'vectorString',
    'impactScore',
    'exploitabilityScore',
    'source'
  ],
  [V2]: [
    'baseScore', // + BaseSeverity
    'vectorString',
    'impactScore',
    'exploitabilityScore'
  ]
};

export const ToggleButtonStyled = styled(ToggleButton)(({ theme, width }) => ({
  color: theme.palette.secondary.main,
  '&.Mui-selected': {
    color: theme.palette.secondary.contrastText,
    backgroundColor: theme.palette.secondary.main
  },
  '&.Mui-selected:hover': {
    backgroundColor: theme.palette.secondary.main
  },
  '&.Mui-disabled': {
    pointerEvents: 'auto'
  }
}));

const NRSeverityAndMetrics = ({ vulnerabilityData, cveDetails, cveDetailsLoading, threshold, scrollTarget, index, scrollableId }) => {
  const { setScrollSpyTabsValue } = useContext(VulnDrawerTabsContext);
  const [cvssVersion, setCvssVersion] = useState(V4);
  const theme = useTheme();

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold,
    target: !!scrollTarget ? scrollTarget : undefined
  });

  useEffect(() => {
    setScrollSpyTabsValue(index, trigger);
  }, [trigger]);

  const handleCvssVersion = (event, newVersion) => {
    !!newVersion && setCvssVersion(newVersion);
  };

  useEffect(() => {
    if (!isEmpty(cveDetails) && !cveDetails[V4]) {
      if (!!cveDetails[V3]) {
        setCvssVersion(V3);
      } else if (!!cveDetails[V2]) {
        setCvssVersion(V2);
      } else {
        setCvssVersion(null);
      }
    }
  }, [cveDetails]);

  function getTooltipTitle(cveDetails, cvssVersion, key, baseSeverity) {
    switch (key) {
      case 'baseScore':
        return cveDetails[cvssVersion]['base'][key] + baseSeverity;
      case 'baseThreatScore':
        return cveDetails[cvssVersion]['base'][key] + baseSeverity;

      case 'vectorString':
        return (
          <Box sx={{ minWidth: theme.spacing(31.25) }}>
            <Box mb={theme.spacing(1)}>{VECTOR}</Box>
            {tooltipEntries.map((name, index) => {
              return (
                <React.Fragment key={`vector-string-tooltip-${index}`}>
                  {!!cveDetails[cvssVersion]['base'][name] && (
                    <Box sx={{ display: 'flex', width: theme.spacing(43.75) }}>
                      <Box sx={{ width: '70%' }}>{TOOLTIP_KEYS[name] || splitCamelCaseWithInitials(name)}</Box>
                      <Box sx={{ width: '30%', textTransform: 'capitalize' }}>{cveDetails[cvssVersion]['base'][name].toLowerCase()}</Box>
                    </Box>
                  )}
                </React.Fragment>
              );
            })}
          </Box>
        );
      default:
        return cveDetails[cvssVersion]['base'][key];
    }
  }

  return (
    <Box id={scrollableId} sx={{ padding: theme.spacing(1.5, 0, 1.5) }}>
      {!cveDetailsLoading && !isEmpty(cveDetails) && (
        <>
          <div>
            <Typography sx={{ margin: theme.spacing(0, 0, 1.5, 0) }} variant="subtitle1">
              {TITLE}
            </Typography>
            <ToggleButtonGroup sx={{ mb: theme.spacing(2) }} value={cvssVersion} exclusive onChange={handleCvssVersion} aria-label="cvss version">
              <ToggleButtonStyled size="small" disabled={!cveDetails[V4]} value={V4} aria-label="Cvss4">
                <Tooltip title={!!cveDetails[V4] ? '' : CVSS_V4 + TOOLTIP}>
                  <div>{CVSS_V4}</div>
                </Tooltip>
              </ToggleButtonStyled>
              <ToggleButtonStyled size="small" disabled={!cveDetails[V3]} value={V3} aria-label="Cvss3">
                <Tooltip title={!!cveDetails[V3] ? '' : CVSS_V3 + TOOLTIP}>
                  <div>{CVSS_V3}</div>
                </Tooltip>
              </ToggleButtonStyled>
              <ToggleButtonStyled size="small" disabled={!cveDetails[V2]} value={V2} aria-label="Cvss2">
                <Tooltip title={!!cveDetails[V2] ? '' : CVSS_V2 + TOOLTIP}>
                  <div>{CVSS_V2}</div>
                </Tooltip>
              </ToggleButtonStyled>
            </ToggleButtonGroup>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <div>
                {!!cveDetails[cvssVersion] &&
                  orderedColumns[cvssVersion]
                    .map((key, index) => {
                      let value = cveDetails[cvssVersion]['base'][key];
                      let extraValue;

                      switch (key) {
                        case 'baseScore':
                          extraValue = !!cveDetails[cvssVersion]['base']['baseSeverity']
                            ? cveDetails[cvssVersion]['base']['baseSeverity']
                            : !!cveDetails[cvssVersion]['base']['severity']
                            ? cveDetails[cvssVersion]['base']['severity']
                            : '';
                          break;
                        case 'baseThreatScore':
                          extraValue = cveDetails[cvssVersion]['base']['baseThreatSeverity'];
                        default:
                          break;
                      }

                      if (!value && !extraValue) return null;

                      if (!!extraValue) {
                        extraValue = capitalizeFirstLetter(extraValue);
                      }

                      if (!!value && key !== 'source' && key !== 'vectorString') {
                        value = capitalizeFirstLetter(value);
                      }

                      return (
                        <Fragment key={`severity-and-metrics-key-container-${index}-${key}`}>
                          <NRKeyValueDataDisplay
                            uniqueKey={`severity-and-metrics-key-value-${index}-${key}`}
                            dataKey={`${!!KEYS[key] ? KEYS[key] : splitCamelCase(key)}`}
                            keyWidth={'30%'}
                            value={
                              <>
                                {value}
                                {!!extraValue && (
                                  <Box component={'span'} sx={{ textTransform: 'none', pl: theme.spacing(0.25) }}>{` ${extraValue}`}</Box>
                                )}
                              </>
                            }
                            showTooltip={true}
                            tooltipValue={getTooltipTitle(cveDetails, cvssVersion, key, ` ${extraValue}`)}
                            valueMaxWidth={'68%'}
                          />
                        </Fragment>
                      );
                    })
                    .filter(Boolean)}
              </div>
            </Box>
          </div>
          <Box mt={theme.spacing(3)}>
            <Box gap={1} display={'flex'} mb={theme.spacing(1)} alignItems={'center'}>
              <Typography variant="subtitle1">{EPSS_SCORE}</Typography>
              <Tooltip placement={'right'} title={EPSS_TOOLTIP_INFO}>
                <InfoOutlined sx={{ color: theme.palette.text.iconInfo, width: theme.spacing(2), height: theme.spacing(2) }} />
              </Tooltip>
            </Box>
            <div>
              <NRKeyValueDataDisplay
                keyWidth={'30%'}
                dataKey={EPSS_SCORE}
                value={showAsPercentile(cveDetails['exploit']?.['epss']?.['score'] || vulnerabilityData?.epssScore)}
              />
              <NRKeyValueDataDisplay
                keyWidth={'30%'}
                dataKey={EPSS_PERCENTILE}
                value={showAsPercentile(cveDetails['exploit']?.['epss']?.['percentile'] || vulnerabilityData?.epssPercentile)}
              />
            </div>
          </Box>
          <Box mt={theme.spacing(3)}>
            <Box gap={1} display={'flex'} mb={theme.spacing(1)} alignItems={'center'}>
              <Typography variant="subtitle1">{CWE}</Typography>
              <Tooltip placement={'right'} title={CWE_TOOLTIP_INFO}>
                <InfoOutlined sx={{ color: theme.palette.text.iconInfo, width: theme.spacing(2), height: theme.spacing(2) }} />
              </Tooltip>
            </Box>
            {!!cveDetails?.problemTypes?.length && (
              <NRShowMore
                maxHeight={300}
                items={cveDetails?.problemTypes?.map((problemType, i) => (
                  <NRLink
                    key={`nr-problem-type-link-${i}-${problemType.name}`}
                    text={!!problemType.name ? `${problemType.name} (${problemType?.value})` : problemType?.value}
                    url={problemType?.url}
                  />
                ))}
              />
            )}
          </Box>
          <Divider style={{ marginTop: '24px' }} />
        </>
      )}
      {cveDetailsLoading && (
        <Box display={'flex'} justifyContent={'center'}>
          <NRLoader />
        </Box>
      )}
    </Box>
  );
};
export default NRSeverityAndMetrics;
